/**
 * Local Network Sync Service
 * 
 * Enables offline mode fallback where waiter devices can connect directly 
 * to the restaurant's main computer/tablet via local network when internet is down.
 * 
 * Features:
 * - Local WebSocket server discovery
 * - Order synchronization between devices
 * - Menu data caching and sync
 * - Real-time order updates within local network
 */

// ==================== LOCAL SYNC SERVICE ====================

class LocalSyncService {
  constructor() {
    this.isHost = false;
    this.connected = false;
    this.socket = null;
    this.peers = [];
    this.localOrders = [];
    this.menuCache = null;
    this.restaurantId = null;
    this.listeners = new Map();
    this.broadcastChannel = null;
    this.hostUrl = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
  }

  // ==================== HOST MODE (Main POS Computer) ====================

  /**
   * Start as host - the main computer that others connect to
   * Note: Actual server must be running on the host machine
   * This registers the device as a host in the local network
   */
  async startAsHost(restaurantId, port = 9200) {
    this.isHost = true;
    this.restaurantId = restaurantId;
    this.hostUrl = `ws://${this.getLocalIP()}:${port}`;
    
    // Use BroadcastChannel for same-origin tabs communication
    this.setupBroadcastChannel();
    
    // Store host info in localStorage for other tabs
    localStorage.setItem('localSync_host', JSON.stringify({
      restaurantId,
      url: this.hostUrl,
      timestamp: Date.now(),
      hostname: window.location.hostname
    }));

    console.log('LocalSync: Started as host');
    this.emit('host_started', { url: this.hostUrl });
    
    return this.hostUrl;
  }

  /**
   * Get local IP address (approximate - may need manual configuration)
   */
  getLocalIP() {
    // In production, this would be configured or discovered
    // For now, return localhost or configured IP
    return localStorage.getItem('localSync_hostIP') || 'localhost';
  }

  /**
   * Set the host IP manually (for local network)
   */
  setHostIP(ip) {
    localStorage.setItem('localSync_hostIP', ip);
  }

  // ==================== CLIENT MODE (Waiter Devices) ====================

  /**
   * Connect to host as a client (waiter device)
   */
  async connectToHost(hostUrl) {
    return new Promise((resolve, reject) => {
      try {
        console.log('LocalSync: Connecting to host:', hostUrl);
        this.socket = new WebSocket(hostUrl);
        this.hostUrl = hostUrl;

        const timeout = setTimeout(() => {
          if (!this.connected) {
            this.socket.close();
            reject(new Error('Connection timeout'));
          }
        }, 10000);

        this.socket.onopen = () => {
          clearTimeout(timeout);
          console.log('LocalSync: Connected to host');
          this.connected = true;
          this.isHost = false;
          this.reconnectAttempts = 0;
          
          // Request sync data
          this.send({ type: 'sync_request' });
          
          this.emit('connected', { hostUrl });
          resolve(true);
        };

        this.socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (e) {
            console.error('LocalSync: Invalid message', e);
          }
        };

        this.socket.onclose = () => {
          console.log('LocalSync: Disconnected from host');
          this.connected = false;
          this.emit('disconnected');
          this.attemptReconnect(hostUrl);
        };

        this.socket.onerror = (error) => {
          clearTimeout(timeout);
          console.error('LocalSync: Connection error', error);
          reject(new Error('Connection failed'));
        };

      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Auto-discover host on local network
   */
  async discoverHost() {
    // Check localStorage for host info
    const hostInfo = localStorage.getItem('localSync_host');
    if (hostInfo) {
      try {
        const info = JSON.parse(hostInfo);
        // Check if host info is recent (within last hour)
        if (Date.now() - info.timestamp < 3600000) {
          return info;
        }
      } catch (e) {
        // Invalid host info
      }
    }

    // Try common local addresses
    const commonPorts = [9200, 9201, 9202];
    const commonIPs = [
      'localhost',
      '192.168.1.1',
      '192.168.0.1',
      '10.0.0.1',
    ];

    // Add IP from manual config
    const configuredIP = localStorage.getItem('localSync_hostIP');
    if (configuredIP) {
      commonIPs.unshift(configuredIP);
    }

    for (const ip of commonIPs) {
      for (const port of commonPorts) {
        try {
          const url = `ws://${ip}:${port}`;
          const found = await this.probeHost(url);
          if (found) {
            return { url, ip, port };
          }
        } catch (e) {
          // Continue trying
        }
      }
    }

    return null;
  }

  /**
   * Probe a potential host
   */
  async probeHost(url, timeout = 2000) {
    return new Promise((resolve) => {
      const ws = new WebSocket(url);
      const timer = setTimeout(() => {
        ws.close();
        resolve(false);
      }, timeout);

      ws.onopen = () => {
        clearTimeout(timer);
        ws.close();
        resolve(true);
      };

      ws.onerror = () => {
        clearTimeout(timer);
        resolve(false);
      };
    });
  }

  // ==================== SYNC OPERATIONS ====================

  /**
   * Sync orders with host/peers
   */
  async syncOrders(orders) {
    if (this.isHost) {
      // Host broadcasts to all clients
      this.localOrders = orders;
      this.broadcast({ type: 'orders_update', orders });
    } else if (this.connected) {
      // Client sends to host
      this.send({ type: 'orders_update', orders });
    }
    
    // Also store locally
    this.storeLocal('orders', orders);
  }

  /**
   * Sync menu data
   */
  async syncMenu(menu) {
    if (this.isHost) {
      this.menuCache = menu;
      this.broadcast({ type: 'menu_update', menu });
    } else if (this.connected) {
      this.send({ type: 'menu_request' });
    }
    
    this.storeLocal('menu', menu);
  }

  /**
   * Create order locally (for offline mode)
   */
  async createLocalOrder(order) {
    order.id = order.id || `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    order.is_local = true;
    order.created_at = new Date().toISOString();
    order.synced = false;

    this.localOrders.push(order);
    this.storeLocal('orders', this.localOrders);

    // Broadcast to peers
    if (this.isHost || this.connected) {
      this.broadcast({ type: 'new_order', order });
    }

    this.emit('order_created', order);
    return order;
  }

  /**
   * Update order status locally
   */
  async updateOrderStatus(orderId, status) {
    const order = this.localOrders.find(o => o.id === orderId);
    if (order) {
      order.status = status;
      order.updated_at = new Date().toISOString();
      this.storeLocal('orders', this.localOrders);

      // Broadcast update
      if (this.isHost || this.connected) {
        this.broadcast({ type: 'order_status', orderId, status });
      }

      this.emit('order_updated', order);
    }
    return order;
  }

  /**
   * Get cached menu
   */
  getMenu() {
    if (this.menuCache) return this.menuCache;
    return this.loadLocal('menu');
  }

  /**
   * Get local orders
   */
  getOrders() {
    if (this.localOrders.length > 0) return this.localOrders;
    this.localOrders = this.loadLocal('orders') || [];
    return this.localOrders;
  }

  /**
   * Get unsynced orders (for when internet comes back)
   */
  getUnsyncedOrders() {
    return this.localOrders.filter(o => !o.synced);
  }

  /**
   * Mark orders as synced
   */
  markOrdersSynced(orderIds) {
    for (const id of orderIds) {
      const order = this.localOrders.find(o => o.id === id);
      if (order) {
        order.synced = true;
      }
    }
    this.storeLocal('orders', this.localOrders);
  }

  // ==================== BROADCAST CHANNEL (Same-Origin Tabs) ====================

  setupBroadcastChannel() {
    if ('BroadcastChannel' in window) {
      this.broadcastChannel = new BroadcastChannel('restaurant_local_sync');
      
      this.broadcastChannel.onmessage = (event) => {
        this.handleMessage(event.data);
      };
    }
  }

  // ==================== MESSAGING ====================

  send(data) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(data));
      return true;
    }
    return false;
  }

  broadcast(data) {
    // Send via WebSocket to connected peers
    this.send(data);
    
    // Also broadcast via BroadcastChannel for same-origin tabs
    if (this.broadcastChannel) {
      this.broadcastChannel.postMessage(data);
    }
  }

  handleMessage(data) {
    switch (data.type) {
      case 'sync_request':
        // Client requesting sync - send current data
        this.send({
          type: 'sync_response',
          orders: this.localOrders,
          menu: this.menuCache
        });
        break;

      case 'sync_response':
        // Received sync data from host
        if (data.orders) {
          this.localOrders = data.orders;
          this.storeLocal('orders', this.localOrders);
        }
        if (data.menu) {
          this.menuCache = data.menu;
          this.storeLocal('menu', this.menuCache);
        }
        this.emit('synced', data);
        break;

      case 'orders_update':
        this.localOrders = data.orders;
        this.storeLocal('orders', this.localOrders);
        this.emit('orders_updated', data.orders);
        break;

      case 'menu_update':
        this.menuCache = data.menu;
        this.storeLocal('menu', this.menuCache);
        this.emit('menu_updated', data.menu);
        break;

      case 'new_order':
        if (!this.localOrders.find(o => o.id === data.order.id)) {
          this.localOrders.push(data.order);
          this.storeLocal('orders', this.localOrders);
          this.emit('order_created', data.order);
        }
        break;

      case 'order_status':
        const order = this.localOrders.find(o => o.id === data.orderId);
        if (order) {
          order.status = data.status;
          this.storeLocal('orders', this.localOrders);
          this.emit('order_updated', order);
        }
        break;

      case 'menu_request':
        if (this.menuCache) {
          this.send({ type: 'menu_update', menu: this.menuCache });
        }
        break;

      default:
        console.log('LocalSync: Unknown message type', data.type);
    }
  }

  // ==================== LOCAL STORAGE ====================

  storeLocal(key, data) {
    try {
      localStorage.setItem(`localSync_${key}`, JSON.stringify(data));
    } catch (e) {
      console.error('LocalSync: Storage error', e);
    }
  }

  loadLocal(key) {
    try {
      const data = localStorage.getItem(`localSync_${key}`);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }

  // ==================== RECONNECTION ====================

  attemptReconnect(url) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`LocalSync: Reconnecting (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
      setTimeout(() => {
        this.connectToHost(url).catch(() => {});
      }, 2000 * this.reconnectAttempts); // Exponential backoff
    } else {
      this.emit('reconnect_failed');
    }
  }

  // ==================== EVENT HANDLING ====================

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (e) {
          console.error('LocalSync: Event handler error', e);
        }
      });
    }
  }

  // ==================== STATUS ====================

  getStatus() {
    return {
      isHost: this.isHost,
      connected: this.connected,
      hostUrl: this.hostUrl,
      peersCount: this.peers.length,
      localOrdersCount: this.localOrders.length,
      unsyncedCount: this.getUnsyncedOrders().length,
      hasMenuCache: !!this.menuCache
    };
  }

  /**
   * Disconnect and cleanup
   */
  disconnect() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }

    this.connected = false;
    this.isHost = false;
    this.emit('disconnected');
  }
}

// Singleton instance
const localSync = new LocalSyncService();

export { LocalSyncService };
export default localSync;
